/**
 * As-reported foreign-ledger SCHEMAS + pure helpers — SCRUM-367 (DS-026 S5, P0, D1).
 *
 * The money-CARRYING half of the as-reported ledger split (D1). The
 * dependency-free value-vocab (`REMITTED_BY_VALUES`, `AS_REPORTED_*_VALUES`,
 * `compareAsReportedLineOrder`) lives in `@artemis/domain`; the Zod schemas that
 * frame the cents amounts + the pure helpers over the resulting structs live
 * HERE, because cents lives here and `@artemis/validators` is the single source
 * of truth for validation (CLAUDE.md rule 3). Mirrors the
 * `RESERVATION_TYPE_INTERNAL_KEY_VALUES` (domain) ⇄ `z.enum(...)` (validators)
 * precedent — no value is duplicated; the schemas re-derive from the domain arrays.
 *
 * ── Money typing (owned decision, extends the tdd-runner money-type note) ─────
 * `amountCents` / `authoritativeTotalCents` / `taxAmountCents` are int-cents
 * validated by {@link asReportedCentsSchema} but their INFERRED TYPE is a bare
 * `number`, NOT the branded `Cents`. Rationale:
 *   1. An `AsReportedLedger` is a VERBATIM transcription of a foreign channel's
 *      stated figures (including negative discount lines), never a value Artemis
 *      COMPUTES. The only operation is {@link checkAuthoritativeTotal}, an EXACT
 *      integer Σ-and-compare (`+` only — never `* / %`, never float), so
 *      CLAUDE.md rule 2's arithmetic ban does not bite here (mirrors `sumCents`,
 *      which itself uses native `+`).
 *   2. The established precedent is `@artemis/domain`'s `worked-example-doc.ts`
 *      (`MoneyStage.amountCents: number`) — foreign/transcribed money in a shared
 *      data type is a bare `number`.
 *   3. Zod's `.brand<'Cents'>()` produces `number & z.BRAND<'Cents'>`, which is a
 *      DIFFERENT nominal brand from the `@artemis/validators` `Cents`
 *      (`number & { __brand: 'Cents' }`); branding here would make the inferred
 *      type assignable from NEITHER a bare integer NOR a `cents()` value, breaking
 *      every producer + the pinned P0 hash-vector / scalar specs (which construct
 *      ledgers from bare integer literals). The int + safe-integer schema is the
 *      single source of truth for "a valid cents figure on this ledger"; the brand
 *      would add no safety and break callers.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { AS_REPORTED_CHARGE_KIND_VALUES, AS_REPORTED_PROVENANCE_VALUES, REMITTED_BY_VALUES, compareAsReportedLineOrder, } from '@artemis/domain';
/**
 * Signed integer-cents schema for the as-reported foreign ledger — the single
 * source of truth for "a valid cents amount on this ledger". Signed (a discount
 * line carries a negative amount) and safe-integer bounded. NOT branded — see
 * the module-level money-typing note; the inferred type is a bare `number`.
 */
const asReportedCentsSchema = z
    .number()
    .int()
    .min(-Number.MAX_SAFE_INTEGER)
    .max(Number.MAX_SAFE_INTEGER);
/**
 * One as-reported charge line: the channel's own itemised figure, transcribed
 * verbatim. `kind`/`remittedBy`/`provenance` re-derive from the `@artemis/domain`
 * value-sets (no duplication). `source: 'channel'` is a pinned literal (this
 * ledger only ever holds channel-reported lines). `.readonly()` — a transcribed
 * line is immutable.
 */
export const asReportedChargeLineSchema = z
    .object({
    kind: z.enum(AS_REPORTED_CHARGE_KIND_VALUES),
    code: z.string().min(1),
    label: z.string().min(1),
    amountCents: asReportedCentsSchema,
    currencyIsReservationCurrency: z.literal(true),
    taxAmountCents: asReportedCentsSchema.nullable(),
    remittedBy: z.enum(REMITTED_BY_VALUES),
    provenance: z.enum(AS_REPORTED_PROVENANCE_VALUES),
    source: z.literal('channel'),
})
    .readonly();
/**
 * The immutable as-reported foreign ledger (§9.3). `authoritativeTotalCents` is
 * the channel's STATED GROSS total, carried verbatim — NOT `Σ(lines)` (D3); the
 * structural Σ-vs-authoritative check is {@link checkAuthoritativeTotal}, a
 * visible flag, never the source of truth. `.readonly()` — immutable once frozen.
 */
export const asReportedLedgerSchema = z
    .object({
    authoritativeTotalCents: asReportedCentsSchema,
    lines: z.array(asReportedChargeLineSchema).readonly(),
    raw: z.record(z.unknown()).readonly(),
})
    .readonly();
/**
 * Structural check (D3, §9.3a): does `Σ(lines.amountCents)` equal the channel's
 * declared `authoritativeTotalCents`? EXACT integer arithmetic — a `.reduce`
 * over integers with native `+` (no float, no `* / %`; CLAUDE.md rule 2-safe).
 * FAIL-SAFE: a malformed line (e.g. `NaN` amount) makes `actualSumCents` NaN, and
 * `NaN === expected` is `false`, so the outcome is a reported MISMATCH — never a
 * crash, never a false `matches: true`.
 *
 * `commission` lines are EXCLUDED from the sum (SCRUM-367 money-guardian): a
 * commission is a channel-RETAINED cut (the gross→net deduction), NOT part of the
 * guest-facing GROSS `authoritativeTotalCents`. Including it would make every
 * commission-bearing ChannelCollect delivery (e.g. an Expedia VCC: gross 54,800,
 * commission −7,500, net 47,300) false-flag a structural discrepancy. `discount`
 * lines STAY in the sum — a discount reduces the guest-facing gross itself. The
 * check therefore verifies "the channel's stated GROSS == Σ its own guest-facing
 * (non-commission) lines", the correct consistency guard for a gross total.
 */
export function checkAuthoritativeTotal(ledger) {
    const expectedCents = ledger.authoritativeTotalCents;
    const actualSumCents = ledger.lines
        .filter((line) => line.kind !== 'commission')
        .reduce((sum, line) => sum + line.amountCents, 0);
    return {
        matches: actualSumCents === expectedCents,
        expectedCents,
        actualSumCents,
        deltaCents: actualSumCents - expectedCents,
    };
}
/**
 * Canonicalise line order (§9.3a.3) so a re-ingested ledger — whatever order the
 * channel sent its lines in — persists in one deterministic order and therefore
 * hashes identically (re-ingest idempotency). Sorts by
 * {@link compareAsReportedLineOrder} (kind-position, then `code`); the sort is
 * STABLE (ECMAScript-2019 `Array.prototype.sort`, Node ≥ 20), so equal
 * `(kind, code)` lines keep their original relative order — the original-index
 * tiebreak. Pure: returns a NEW ledger; the input is untouched. The booking
 * snapshot factory applies the SAME comparator (via `@artemis/domain`, since a
 * domain file cannot import this validators runtime) so the two producers of a
 * canonical ledger can never drift.
 */
export function normalizeAsReportedLedger(ledger) {
    const lines = [...ledger.lines].sort(compareAsReportedLineOrder);
    return { ...ledger, lines };
}
//# sourceMappingURL=as-reported-ledger.js.map